import { ChevronDown, ChevronUp, Download, FileText, Sheet } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { CSVLink } from 'react-csv'
import { type Data, type Headers } from 'react-csv/lib/core'

import { Button, ButtonLink, DropdownMenu, Modal } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney, getFormattedDate } from 'utils'
import { getNextMonthName, getPriorMonthName } from 'utils/billing'
import { usePostEmployersReportingEmployerBillingInvoice } from 'utils/web_api'
import {
  type EmployersEmployerInvoice,
  type EmployersMemberInvoiceDetail,
  type Money,
} from 'utils/web_api.schemas'

function formatMoneyValue (money: Money | undefined): string {
  const value = convertGrapeMoneyToNumber(money)
  if (value === 0) {
    return '---'
  }
  return formatMoney(value)
}

interface EmployerInvoiceDropdownProps {
  invoice: EmployersEmployerInvoice
  enrichedMemberInvoices: EmployersMemberInvoiceDetail[]
  showPlanYear: boolean
  reportDownloadUrl?: string | null
}

export function EmployerInvoiceDropdown ({
  invoice,
  enrichedMemberInvoices,
  showPlanYear,
  reportDownloadUrl,
}: EmployerInvoiceDropdownProps) {
  const [showReportGenerationModal, setShowReportGenerationModal] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const createReportMutation = usePostEmployersReportingEmployerBillingInvoice()

  const periodStartDate = invoice.period_start_date
  const periodEndDate = invoice.period_end_date

  const startDateUTC = periodStartDate
    ? getFormattedDate(periodStartDate, { timeZone: 'UTC' })
    : 'NA'
  const endDateUTC = periodEndDate
    ? getFormattedDate(periodEndDate, { timeZone: 'UTC' })
    : 'NA'

  const nextMonthName = periodEndDate ? getNextMonthName(periodEndDate) : ''
  const priorMonthName = periodStartDate ? getPriorMonthName(periodStartDate) : ''

  const csvHeaders: Headers = useMemo(() => {
    const headers: Headers = [
      { label: 'Employee ID', key: 'employee_id' },
      { label: 'Full Name', key: 'full_name' },
    ]

    if (showPlanYear) {
      headers.push({ label: 'Plan Year', key: 'plan_year' })
    }

    headers.push(
      { label: `Pre-payment for ${nextMonthName} insurance coverage`, key: 'prefunding' },
      { label: `Payment for ${priorMonthName} insurance coverage`, key: 'premiums' },
      { label: `Changes in ${priorMonthName} insurance coverage`, key: 'coverage_corrections' },
      { label: 'Qualified medical expenses', key: 'other' },
      { label: 'Total healthcare cost', key: 'total' },
      { label: 'Source ID', key: 'native_employee_id' }
    )

    return headers
  }, [showPlanYear, nextMonthName, priorMonthName])

  const csvData: Data = useMemo(() => {
    if (enrichedMemberInvoices.length === 0) {
      return []
    }

    return enrichedMemberInvoices.map((memberInvoice) => {
      const row: Record<string, string | number | undefined> = {
        employee_id: memberInvoice.employee_id || '',
        full_name: memberInvoice.full_name || '',
        prefunding: formatMoneyValue(memberInvoice.amounts?.total_insurance_premiums),
        premiums: formatMoneyValue(memberInvoice.amounts?.premiums),
        coverage_corrections: formatMoneyValue(memberInvoice.amounts?.coverage_corrections),
        other: formatMoneyValue(memberInvoice.amounts?.other),
        total: formatMoneyValue(memberInvoice.amounts?.total),
        native_employee_id: memberInvoice.native_employee_id || '',
      }

      if (showPlanYear) {
        row.plan_year = memberInvoice.plan_year ?? ''
      }

      return row
    })
  }, [enrichedMemberInvoices, showPlanYear])

  const handleGenerateInvoiceReport = async () => {
    setIsGeneratingReport(true)

    try {
      await createReportMutation.mutateAsync({
        data: {
          employer_invoice_id: invoice.id,
        }
      })

      setShowReportGenerationModal(false)
    } catch (error) {
      console.error('Failed to create report:', error)
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const isDisabled = enrichedMemberInvoices.length === 0

  const csvLinkRef = React.useRef<any>(null)
  const buttonLinkRef = React.useRef<any>(null)

  const handleDownloadCSVClick = () => {
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click()
    }
    return false
  }

  const handleDownloadPDFClick = () => {
    if (reportDownloadUrl && buttonLinkRef.current) {
      buttonLinkRef.current.click()
    } else {
      setShowReportGenerationModal(true)
    }
    return false
  }

  const filename = periodStartDate && periodEndDate
    ? `invoice-${periodStartDate}_${periodEndDate}.csv`
    : 'invoice.csv'

  return (
    <>
      <DropdownMenu
        trigger={({ open }) => (
          <Button
            variant='tertiary'
            disabled={isDisabled}
            rightIcon={open ? ChevronUp : ChevronDown}
            leftIcon={Download}
          >
            Download
          </Button>
        )}
        align='end'
      >
        <DropdownMenu.Item
          leftIcon={Sheet}
          onSelect={handleDownloadCSVClick}
        >
          CSV Report
        </DropdownMenu.Item>

        <DropdownMenu.Item
          leftIcon={FileText}
          onSelect={handleDownloadPDFClick}
        >
          Invoice PDF
        </DropdownMenu.Item>
      </DropdownMenu>

      <CSVLink
        ref={csvLinkRef}
        data={csvData}
        headers={csvHeaders}
        filename={filename}
        target='_blank'
        style={{ display: 'none' }}
        data-testid='csv-download-link'
      />

      <ButtonLink
        ref={buttonLinkRef}
        href={reportDownloadUrl ?? undefined}
        download
        disabled={!reportDownloadUrl}
        target='_blank'
        style={{ display: 'none' }}
      >
        PDF Download
      </ButtonLink>

      <Modal
        open={showReportGenerationModal}
        onOpenChange={setShowReportGenerationModal}
        width='medium'
      >
        <Modal.Header>
          <Modal.Title>Request Invoice PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='space-y-4'>
            <div>
              <p className='mb-3'>
                This report includes a breakdown of your invoice covering the period from {startDateUTC} to {endDateUTC}.
              </p>
            </div>

            <p className='text-sm'>
              This report may take a few minutes to generate.
              You'll receive an email with a download link once it's ready.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end gap-3 w-full'>
            <Modal.Button
              variant='secondary'
              disabled={isGeneratingReport}
            >
              Cancel
            </Modal.Button>
            <Button
              variant='primary'
              onClick={handleGenerateInvoiceReport}
              disabled={isGeneratingReport}
              leftIcon={isGeneratingReport ? undefined : FileText}
            >
              {isGeneratingReport ? 'Generating...' : 'Generate report'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}
